import Constants from 'expo-constants';
import { useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

import { supabase } from '@/lib/supabase';

export type UpdateStatus =
  | { type: 'none' }
  | { type: 'optional'; startUpdate: () => void }
  | { type: 'forced';   startUpdate: () => void }
  | { type: 'ota';      startUpdate: () => void; changelog?: string | null };

const STORE_URL = 'https://play.google.com/store/apps/details?id=com.heirapsia.app';

function parseVersion(v: string): number[] {
  return v.split('.').map((n) => parseInt(n, 10) || 0);
}

function compareVersions(a: string, b: string): number {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function useUpdateCheck() {
  const [status, setStatus] = useState<UpdateStatus>({ type: 'none' });

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const currentVersion = Constants.expoConfig?.version ?? '0.0.0';

    async function check() {
      const { data, error } = await supabase
        .from('app_config')
        .select('key, value')
        .in('key', ['latest_version', 'min_version']);

      if (error || !data) return;

      const cfg: Record<string, string> = {};
      data.forEach((row) => { cfg[row.key] = row.value; });

      const latestVersion = cfg['latest_version'] ?? currentVersion;
      const minVersion    = cfg['min_version']    ?? '0.0.0';
      const startUpdate   = () => void Linking.openURL(STORE_URL);

      if (compareVersions(currentVersion, minVersion) < 0) {
        setStatus({ type: 'forced', startUpdate });
      } else if (compareVersions(currentVersion, latestVersion) < 0) {
        setStatus({ type: 'optional', startUpdate });
      }
    }

    void check();
  }, []);

  return status;
}
